/**
 * Officers assigned to a job.
 *
 * Assignment governs ORDERING in the scheduling picker, not eligibility — any
 * eligible officer can still be scheduled in a coverage emergency.
 *
 * Adding the job's client to an officer's accounts is a PROMPT, never a silent
 * write: employees.client_ids is the supervisory-access and evaluation-routing
 * spine and nothing here touches it without an explicit click.
 */
import React, { useEffect, useState } from 'react';
import { assignmentsAPI, employeesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UserPlus, Star, X } from 'lucide-react';

export default function JobOfficersEditor({ jobId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const load = async () => {
    try {
      const { data } = await assignmentsAPI.list({ job_id: jobId });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load assignments');
    }
  };

  useEffect(() => {
    if (!jobId) return;
    load();
    employeesAPI.list({ employment_status: 'Active' })
      .then(({ data }) => setEmployees(Array.isArray(data) ? data : (data?.employees || [])))
      .catch(() => setEmployees([]));
  }, [jobId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async () => {
    if (!pick) { toast.error('Pick an officer'); return; }
    setBusy(true);
    try {
      const { data } = await assignmentsAPI.create({
        employee_id: pick, job_id: jobId, is_primary: false,
      });
      toast.success('Officer assigned');
      if (data.client_suggestion) setSuggestion({ ...data.client_suggestion, assignment_id: data.id });
      setPick('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign');
    } finally { setBusy(false); }
  };

  const acceptSuggestion = async () => {
    try {
      await assignmentsAPI.acceptClientSuggestion(suggestion.assignment_id);
      toast.success('Client added to the officer\'s accounts');
      setSuggestion(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add the client');
    }
  };

  const setPrimary = async (row) => {
    try { await assignmentsAPI.setPrimary(row.id, !row.is_primary); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const unassign = async (row) => {
    try { await assignmentsAPI.unassign(row.id); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not unassign'); }
  };

  const assignedIds = new Set(rows.map(r => r.employee_id));

  return (
    <div className="space-y-4" data-testid="job-officers">
      <p className="text-sm text-fg-muted max-w-2xl">
        Officers who normally work this job. This orders the scheduling picker — it does not
        limit who can be scheduled when someone calls in.
      </p>

      {canEdit && (
        <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[280px]">
            <Label>Officer</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="officer-pick"><SelectValue placeholder="Select an officer…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.filter(e => !assignedIds.has(e.id)).map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {[e.first_name, e.last_name].filter(Boolean).join(' ')}
                    {e.employee_number ? ` · ${e.employee_number}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={assign} disabled={busy} data-testid="officer-assign">
            <UserPlus className="w-4 h-4 mr-2" />Assign
          </Button>
        </CardContent></Card>
      )}

      {suggestion && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="client-suggestion">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">{suggestion.client_name} is not on this officer's accounts</p>
              <p className="text-xs text-fg-muted max-w-xl">{suggestion.message}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSuggestion(null)}
                data-testid="client-suggestion-dismiss">Not now</Button>
              <Button size="sm" onClick={acceptSuggestion} data-testid="client-suggestion-accept">
                Add the client
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Officer</TableHead>
            <TableHead>Number</TableHead>
            <TableHead>Assigned</TableHead>
            {canEdit && <TableHead className="w-24 text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {!rows.length ? (
              <TableRow><TableCell colSpan={4} className="p-6 text-sm text-fg-muted" data-testid="officers-empty">
                Nobody assigned yet.
              </TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} data-testid={`officer-row-${r.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {r.employee_name || r.employee_id}
                    {r.is_primary && <Badge variant="outline">Primary</Badge>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.employee_number || '—'}</TableCell>
                <TableCell className="text-xs text-fg-muted">
                  {(r.assigned_at || '').slice(0, 10)}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => setPrimary(r)}
                      title={r.is_primary ? 'Clear primary' : 'Make primary'}
                      data-testid={`officer-primary-${r.id}`}>
                      <Star className={`w-4 h-4 ${r.is_primary ? 'fill-current text-amber-500' : ''}`} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => unassign(r)}
                      data-testid={`officer-unassign-${r.id}`}><X className="w-4 h-4" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
